import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Bell, Info, Settings } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { useDeviceOrganization } from "@/hooks/use-device-organization";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useQuery } from "@tanstack/react-query";
import { SharedWorkflowSettings } from "./ai-workflows/SharedWorkflowSettings";
import { WorkflowSelector, AiWorkflowType } from "./ai-workflows/WorkflowSelector";
import { QuickReasoningWorkflow } from "./ai-workflows/QuickReasoningWorkflow";
import { DeepReasoningWorkflow } from "./ai-workflows/DeepReasoningWorkflow";
import { GeminiReasoningWorkflow } from "./ai-workflows/GeminiReasoningWorkflow";
import { ChainOfThoughtWorkflow } from "./ai-workflows/ChainOfThoughtWorkflow";
import { ObjectDetectionWorkflow } from "./ai-workflows/ObjectDetectionWorkflow";
import { ChangeDetectionWorkflow } from "./ai-workflows/ChangeDetectionWorkflow";
import { CustomWorkflow } from "./ai-workflows/CustomWorkflow";
import { BoundaryBox } from "./BoundingBoxEditor";
import { AlertSubscribersContent, toggleAppAlerts } from "./ai-workflows/DeviceAlertSubscribers";
import { AiModelCard } from "./ai-workflows/AiModelCard";
import { ImagePrivacyCard } from "./ai-workflows/ImagePrivacyCard";
import { changeAiEnabled, modelForEnabledAi, modelToApplyFromTemplate, AI_MODEL_OPTIONS, buildshipModelValue, needsModelRepair } from './ai-workflows/aiModelSettings';
import { updateDeviceField } from '@/utils/updateDeviceField';

interface DeviceNotificationsProps {
  device: any;
  deviceId: string;
  onUpdate: () => Promise<void>;
}

export const DeviceNotifications = ({ device, deviceId, onUpdate }: DeviceNotificationsProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [emailValue, setEmailValue] = useState(device?.notification_email || "");
  const defaultQueryTemplate = `SCOPE: 

<DESCRIBE THE SCENARIO HERE>

RESULT: 

Return True IF <ENTER REASON FOR ALERT HERE>

Otherwise return FALSE

SUBJECT:

True Subject Heading (3–5 words): <ENTER ALERT DESCRIPTION HERE>

False Subject Heading (3–5 words): <ENTER STANDARD DESCRIPTION HERE>`;

  const [questionValue, setQuestionValue] = useState(() => {
    const existing = device?.notification_question || "";
    return existing.toLowerCase() === "enter question here" ? defaultQueryTemplate : existing;
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(device?.enable_notifications === true);
  const [surveillanceEnabled, setSurveillanceEnabled] = useState(device?.enable_surveillance_mode === true);
  const [aiWorkflow, setAiWorkflow] = useState<AiWorkflowType>((device?.ai_workflow as AiWorkflowType) || '');
  const [aiModel, setAiModel] = useState<string | null>(device?.notification_ai_model ?? null);
  const [isSavingAi, setIsSavingAi] = useState(false);
  const aiSavePending = useRef(false);
  const repairAttempt = useRef<string | null>(null);
  const isMobile = useIsMobile();
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { logChange } = useDeviceChangeLog();
  const { data: userOrganizationId } = useUserOrganization();
  const { data: deviceOrganizationId } = useDeviceOrganization(deviceId);
  // Prefer the device's owning organization; fall back to logged-in user's org.
  const organizationId = deviceOrganizationId ?? userOrganizationId;

  const { data: customWorkflowAvailable } = useQuery({
    queryKey: ['org-custom-workflow-available', organizationId],
    queryFn: async () => {
      if (!organizationId) return false;
      const { data, error } = await supabase
        .from('organizations')
        .select('custom_workflow_id')
        .eq('id', organizationId)
        .single();
      if (error || !data?.custom_workflow_id) return false;
      return data.custom_workflow_id.trim().length > 0;
    },
    enabled: !!organizationId,
  });

  const [isTogglingAlerts, setIsTogglingAlerts] = useState(false);
  const { role: currentRole, isSystemAdmin: currentIsSystemAdmin } = useSimpleRole();
  const canManageAlerts = currentIsSystemAdmin;
  const appAlertsOn = device?.app_alerts_enabled === true;

  const handleToggleAppAlerts = async (on: boolean) => {
    setIsTogglingAlerts(true);
    try {
      await toggleAppAlerts(deviceId, device, on, updateField, onUpdate);
    } catch (e: any) {
      toast.error(`Couldn't change alert sending: ${e?.message || "unknown error"}`);
    } finally {
      setIsTogglingAlerts(false);
    }
  };

  const updateField = async (field: string, value: string | number) => {
    setIsUpdating(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", field);
      params.append("value", value.toString());
      logChange(deviceId, field, value.toString());
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!response.ok) throw new Error("Failed to update device");
      await onUpdate();
      toast.success("Device updated successfully");
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error("Failed to update device");
    } finally {
      setIsUpdating(false);
    }
  };

  // Disable surveillance when notifications off
  useEffect(() => {
    if (!notificationsEnabled && surveillanceEnabled) {
      updateField("enableSurveilance", "false");
      setSurveillanceEnabled(false);
    }
  }, [notificationsEnabled, surveillanceEnabled, deviceId]);


  const persistAiModel = async (model: string | null) => {
    await updateDeviceField(deviceId, 'notificationAiModel', buildshipModelValue(model));
    logChange(deviceId, 'notificationAiModel', buildshipModelValue(model));
    const { data, error } = await supabase
      .from('device_details')
      .update({ notification_ai_model: model })
      .eq('device_id', deviceId)
      .select('notification_ai_model')
      .single();
    if (error) throw error;
    if (!data || data.notification_ai_model !== model) throw new Error('Model save could not be confirmed');
    setAiModel(data.notification_ai_model);
  };

  const runAiSave = async (operation: () => Promise<void>) => {
    if (aiSavePending.current || !canEditDeviceConfig) return;
    aiSavePending.current = true;
    setIsSavingAi(true);
    try {
      await operation();
      await onUpdate();
      toast.success('AI settings saved');
    } catch (error) {
      console.error('Error saving AI settings:', error);
      toast.error('Could not save AI settings. Please try again.');
    } finally {
      aiSavePending.current = false;
      setIsSavingAi(false);
    }
  };

  const saveAiModel = (model: string) => runAiSave(() => persistAiModel(model));

  const handleNotificationsChange = (checked: boolean) => runAiSave(async () => {
    repairAttempt.current = null;
    // Use the saved camera record, not the screen value; empty falls back to Flash.
    const savedModel = (device?.notification_ai_model as string | null | undefined) ?? null;
    await changeAiEnabled(checked, savedModel, {
      saveModel: persistAiModel,
      setEnabled: async enabled => {
        await updateDeviceField(deviceId, 'enableNotifications', enabled ? 1 : 0);
        logChange(deviceId, 'enableNotifications', enabled ? '1' : '0');
        const { error } = await supabase.from('device_details').update({ enable_notifications: enabled }).eq('device_id', deviceId);
        if (error) throw error;
        setNotificationsEnabled(enabled);
      },
      readModel: async () => {
        const { data } = await supabase.from('device_details').select('notification_ai_model').eq('device_id', deviceId).single();
        return data?.notification_ai_model ?? null;
      },
    });
  });

  // Refresh from the actual camera record, never from a stale refresh during a save.
  useEffect(() => {
    if (aiSavePending.current) return;
    setAiModel(device?.notification_ai_model ?? null);
    setNotificationsEnabled(device?.enable_notifications === true);
  }, [deviceId, device?.notification_ai_model, device?.enable_notifications]);

  // Repair legacy enabled cameras once per record state; read-only viewers never write.
  useEffect(() => {
    if (!needsModelRepair({ recordEnabled: device?.enable_notifications, localEnabled: notificationsEnabled,
        model: device?.notification_ai_model, workflow: aiWorkflow }) || !canEditDeviceConfig || aiSavePending.current) return;
    const key = `${deviceId}:${aiWorkflow}`;
    if (repairAttempt.current === key) return;
    repairAttempt.current = key;
    void runAiSave(() => persistAiModel(modelForEnabledAi(null)));
  }, [deviceId, device?.enable_notifications, device?.notification_ai_model, aiWorkflow, canEditDeviceConfig, notificationsEnabled]);

  const handleSurveillanceChange = async (checked: boolean) => {
    setSurveillanceEnabled(checked);
    await updateField("enableSurveilance", checked ? 1 : 0);
  };

  const handleWorkflowChange = async (value: AiWorkflowType) => {
    setAiWorkflow(value);
    await updateField("notificationAiWorkflow", value);
  };

  const handleSaveBoundingBoxes = async (boxes: BoundaryBox[]) => {
    setIsUpdating(true);
    try {
      const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('get-buildship-token');
      if (tokenError || !tokenResponse?.success || !tokenResponse?.token) throw new Error('Failed to get auth token');

      const response = await fetch('https://api.snapshot.systems/updateBoundingBoxes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenResponse.token}` },
        body: JSON.stringify({ deviceId, boundaryBoxes: boxes }),
      });
      if (!response.ok) throw new Error(`API call failed with status ${response.status}`);

      const { error: dbError } = await supabase.from('device_details').update({ boundary_boxes: boxes as unknown as any }).eq('device_id', deviceId);
      if (dbError) console.error('Database save error:', dbError);

      toast.success('ROI settings saved successfully');
      await onUpdate();
    } catch (error) {
      console.error('Error saving bounding boxes:', error);
      toast.error(`Failed to save ROI settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleGenerateQuery = async () => {
    setIsUpdating(true);
    try {
      const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('get-buildship-token');
      if (tokenError || !tokenResponse?.success || !tokenResponse?.token) throw new Error('Failed to get auth token');

      const response = await fetch('https://api.snapshot.systems/generateAiQuery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenResponse.token}` },
        body: JSON.stringify({ deviceID: deviceId, text: questionValue }),
      });
      if (!response.ok) throw new Error(`API call failed with status ${response.status}`);

      const responseText = await response.text();
      let generatedQuery: string | null = null;
      try {
        const result = JSON.parse(responseText);
        generatedQuery = result.query || result.generatedQuery || result.text || result.data?.query;
      } catch { generatedQuery = responseText.trim(); }

      if (!generatedQuery) throw new Error('API did not return a valid query');
      setQuestionValue(generatedQuery);
      await updateField('notification_question', generatedQuery);
      toast.success("AI query generated successfully");
      try { await onUpdate(); } catch {}
    } catch (error: any) {
      console.error('Error generating AI query:', error);
      toast.error(`Failed to generate AI query: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const notification = device?.notification || { result: "Not available", reason: "Not available", message: "Not available", subject: "Not available" };

  const handleDeepReasoningGenerateQuery = async () => {
    const tags = Array.isArray(device?.notification_data_tags)
      ? device.notification_data_tags.join(", ")
      : "None";
    const boxes = device?.boundary_boxes as BoundaryBox[] | undefined;
    const colors = boxes && boxes.length > 0
      ? boxes.map((b: BoundaryBox) => b.colour).join(", ")
      : "None";
    const enrichedText = `AI Query:\n${questionValue}\n\nData Tags:\n${tags}\n\nAreas of Interest:\n${colors}`;

    setIsUpdating(true);
    try {
      const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('get-buildship-token');
      if (tokenError || !tokenResponse?.success || !tokenResponse?.token) throw new Error('Failed to get auth token');

      const response = await fetch('https://api.snapshot.systems/generateAiQuery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tokenResponse.token}` },
        body: JSON.stringify({ deviceID: deviceId, text: enrichedText }),
      });
      if (!response.ok) throw new Error(`API call failed with status ${response.status}`);

      const responseText = await response.text();
      let generatedQuery: string | null = null;
      try {
        const result = JSON.parse(responseText);
        generatedQuery = result.query || result.generatedQuery || result.text || result.data?.query;
      } catch { generatedQuery = responseText.trim(); }

      if (!generatedQuery) throw new Error('API did not return a valid query');
      setQuestionValue(generatedQuery);
      await updateField('notification_question', generatedQuery);
      toast.success("AI query generated successfully");
      try { await onUpdate(); } catch {}
    } catch (error: any) {
      console.error('Error generating AI query:', error);
      toast.error(`Failed to generate AI query: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const workflowProps = {
    device,
    deviceId,
    questionValue,
    onQuestionChange: setQuestionValue,
    onSaveQuery: () => updateField("notificationQuestion", questionValue),
    onGenerateQuery: aiWorkflow === 'quick_reasoning' || aiWorkflow === 'chain_of_thought'
      ? handleGenerateQuery
      : handleDeepReasoningGenerateQuery,
    onSaveBoundingBoxes: handleSaveBoundingBoxes,
    isUpdating: isUpdating || isSavingAi,
    canEdit: canEditDeviceConfig,
    notification,
    updateField,
    aiWorkflow,
    organizationId,
    onTemplateModel: (model: string) => {
      const next = modelToApplyFromTemplate(notificationsEnabled, aiModel, model);
      if (!next) return;
      const label = AI_MODEL_OPTIONS.find(o => o.value === next)?.label || next;
      void runAiSave(async () => { await persistAiModel(next); toast.info(`AI model switched to ${label} for this query`); });
    },
  };

  const WorkflowComponent = ({
    'universal-uk-v1': GeminiReasoningWorkflow,
    'highways-uk-v1': GeminiReasoningWorkflow,
    'environmental-uk-v1': GeminiReasoningWorkflow,
    quick_reasoning: QuickReasoningWorkflow,
    deep_reasoning: DeepReasoningWorkflow,
    'gemini-3.1-pro-preview': GeminiReasoningWorkflow,
    chain_of_thought: ChainOfThoughtWorkflow,
    object_detection: ObjectDetectionWorkflow,
    change_detection: ChangeDetectionWorkflow,
    custom_workflow: CustomWorkflow,
  } as Record<string, React.ComponentType<any>>)[aiWorkflow] ?? (GeminiReasoningWorkflow as React.ComponentType<any>);

  return (
    <div className="p-2">
      {/* Header */}
      <div className={`flex items-center justify-between mb-4 ${isMobile ? 'flex-col space-y-3' : ''}`}>
        <div className="flex items-center">
          <Bell className="h-5 w-5 text-brand-500 mr-2" />
          <h2 className="text-lg font-semibold">AI Setup</h2>
        </div>
      </div>

      {!canEditDeviceConfig && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Read-only Mode</AlertTitle>
          <AlertDescription>Only administrators can modify notification settings. Contact your admin to make changes.</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {/* Card 1: Image Processing Workflow (always active) */}
        <Card className="border bg-muted/50">
          <CardContent className="py-4 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-4 w-4 text-brand-500" />
              <span className="text-sm font-semibold">Image Processing Workflow</span>
            </div>
            <WorkflowSelector
              value={aiWorkflow}
              onChange={handleWorkflowChange}
              disabled={isUpdating || isSavingAi || !canEditDeviceConfig}
              customWorkflowAvailable={!!customWorkflowAvailable}
            />
            <ImagePrivacyCard device={device} deviceId={deviceId} updateField={updateField} embedded />
          </CardContent>
        </Card>

        {/* Card 2: AI Model — hidden for Custom workflow, which preselects its model */}
        {aiWorkflow !== 'custom_workflow' && (
          <AiModelCard
            enabled={notificationsEnabled}
            model={aiModel}
            onEnabledChange={handleNotificationsChange}
            onModelChange={(m) => saveAiModel(m)}
            disabled={isUpdating || isSavingAi || !canEditDeviceConfig}
            isMobile={isMobile}
          />
        )}

        <div className={`space-y-4 ${!notificationsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Card 2: Notifications */}
        <Card className="border bg-muted/50">
          <CardContent className="py-4 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-brand-500" />
                <span className="text-sm font-semibold">Notifications</span>
              </div>
              {canManageAlerts && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Enable Advanced Notifications</Label>
                  <Switch
                    checked={appAlertsOn}
                    onCheckedChange={handleToggleAppAlerts}
                    disabled={isTogglingAlerts}
                  />
                </div>
              )}
            </div>

            {appAlertsOn ? (
              <AlertSubscribersContent deviceId={deviceId} device={device} />
            ) : (
              <SharedWorkflowSettings
                emailValue={emailValue}
                onEmailChange={setEmailValue}
                onEmailSave={() => updateField("notificationEmail", emailValue)}
                surveillanceEnabled={surveillanceEnabled}
                onSurveillanceChange={handleSurveillanceChange}
                notificationsEnabled={notificationsEnabled}
                isUpdating={isUpdating}
                canEdit={canEditDeviceConfig}
              />
            )}
          </CardContent>
        </Card>

          <WorkflowComponent {...workflowProps} organizationId={organizationId} />
        </div>
      </div>
    </div>
  );
};

export default DeviceNotifications;
